import { Injectable } from '@angular/core';
import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import { DietSettingObject } from '@libs/types/src/services/object-type/diet/diet-setting.object';
import { DietSettingUpsertInput } from '@libs/types/src/services/input-type/diet/diet-setting.upsert-input';
import { DietUserViewUpsertInput } from '@libs/types/src/services/input-type/diet/diet-user-view.upsert-input';
import { DietUserViewObject } from '@libs/types/src/services/object-type/diet/diet-user-view.object';
import { DietSupplierSearchInput } from '@libs/types/src/services/input-type/diet/diet-supplier.search-input';
import { DietUpsertInput } from '@libs/types/src/services/input-type/diet/diet.upsert-input';
import { DietGridObject, DietObject } from '@libs/types/src/services/object-type/diet/diet.object';
import { filterFrag } from './types/filter.types';
import { GridInput } from '@it2go/data-grid';
import { DietOrderGridObject } from '@libs/types/src/services/object-type/diet/diet-order.object';
import { DietOrderUpsertInput } from '@libs/types/src/services/input-type/diet/diet-order.upsert-input';
import { DietOrderPrintInput } from '@libs/types/src/services/input-type/diet/diet-order.print-input';
import { DietOrderWorkUpsertInput } from '@libs/types/src/services/input-type/diet/diet-order-work.upsert-input';
import { OkObject } from '@it2go/types';

@Injectable({ providedIn: 'root' })
export class DietGqlService extends GqlServiceV2 {

    public async getDiets(input: GridInput, fragment: string): Promise<DietGridObject> {
        return this.query(
            `
                query($input: GridInput!) {
                    diet {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getDietOrders(input: GridInput, fragment: string): Promise<DietOrderGridObject> {
        return this.query(
            `
                query($input: GridInput!) {
                    dietOrder {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getDietFolders(input: GridInput, fragment: string): Promise<DietOrderGridObject> {
        return this.query(
            `
                query($input: GridInput!) {
                    dietOrder {
                        folderFilter(input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async upsertDietOrders(input: DietOrderUpsertInput[], fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate(
            `
                mutation($input: [DietOrderUpsertInput!]!) {
                    dietOrder {
                        batchUpsert(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async upsertDietWorkOrders(input: DietOrderWorkUpsertInput[], fragment: string = 'ok'): Promise<OkObject> {
        return this.mutate(
            `
                mutation($input: [DietOrderWorkUpsertInput!]!) {
                    dietOrder {
                        batchUpsertWorks(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getDietServiceSuppliers(input: DietSupplierSearchInput): Promise<string[]> {
        return this.query(
            `
                query ($input: DietSupplierSearchInput!) {
                    diet {
                        serviceSuppliers (input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public async upsertDiet(input: DietUpsertInput, fragment: string): Promise<DietObject> {
        return this.mutate(
            `
                mutation ($input: DietUpsertInput!) {
                    diet {
                        upsert (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getDietSetting(input: IdInput, fragment: string) {
        return this.query<DietSettingObject>(
            `
                query ($input: IdInput!) {
                    dietSetting {
                        current(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async upsertDietSetting(input: DietSettingUpsertInput, fragment: string) {
        return this.mutate<DietSettingUpsertInput>(
            `
                mutation ($input: DietSettingUpsertInput!) {
                    dietSetting {
                        upsert(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getDietUserView(input: IdInput, fragment: string) {
        return this.query<DietUserViewObject>(
            `
                query ($input: IdInput!) {
                    dietUserView {
                        current(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async upsertDietUserView(input: DietUserViewUpsertInput, fragment: string) {
        return this.mutate<DietSettingUpsertInput>(
            `
                mutation ($input: DietUserViewUpsertInput!) {
                    dietUserView {
                        upsert(input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async printDietOrders(input: DietOrderPrintInput): Promise<string> {
        return this.query(
            `
                query ($input: DietOrderPrintInput!) {
                    dietOrder {
                        print (input: $input)
                    }
                }
            `,
            { input },
        );
    }

}
